import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, X } from "lucide-react";

// Tiny chime (data-uri) — short pleasant beep
const CHIME_DATA_URI = "data:audio/wav;base64,UklGRrIDAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YY4DAACAgYKDhIaIio2QlJicoaass7nAyM/X3+fv9v0CCQ8VGyAlKi42OUFGSlBVWVxhZGdpa21vcXJzdHV2d3h4eXl5eXl5eHh3dnVzcW9tamhlYV5aV1RQTEhEQDw4MzAsKCQfHBcUEAwIBQH++vbz8O3p5uPg3dvY1tTSz87NzMrJyMfHxsbGxsbHx8jJysvNz9HT1dfa3eDi5efp7O7w8/X3+fv9/v8AAQECAwQEBQYHBwgJCQoLCwwNDQ4PDxAREhITExQVFRYXFxgZGRobGxwdHR4fHyAhISIjIyQlJSYnJygpKSorKywtLS4vLzAxMTIzMzQ1NTY3Nzg5OTo7Ozw9PT4/P0BBQUJDQ0RFRUZHR0hJSUpLS0xNTU5PT1BRUVJTU1RVVVZXV1hZWVpbW1xdXV5fX2BhYWJjY2RlZWZnZ2hpaWprbGxtbm5vcHFxc3R1dXZ4eHl7fH2";

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "à l'instant";
    if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
    if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
    return `il y a ${Math.floor(s / 86400)} j`;
  } catch { return ""; }
}

export default function NotificationBell({ variant = "default" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [connError, setConnError] = useState(false);
  const lastIdRef = useRef(null);
  const audioRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/admin/notifications", {
        // Hint to CDN/proxies — Vercel/Cloudflare must not cache live polling
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
        params: { _t: Date.now() },
      });
      const newest = r.data.items[0]?.id;
      const previousId = lastIdRef.current;
      if (previousId && newest && newest !== previousId && r.data.items[0]?.sound) {
        try { audioRef.current && audioRef.current.play().catch(() => {}); } catch {}
      }
      lastIdRef.current = newest;
      setItems(r.data.items);
      setUnread(r.data.unread || 0);
      setConnError(false);
    } catch (e) {
      // Surface the failure instead of silently swallowing it (this was the prod-only bug):
      // - 401 → token expired → redirect to login
      // - network/5xx → show a small red dot on the bell so the admin notices
      const status = e?.response?.status;
      console.warn("[NotificationBell] poll failed", status || e?.message);
      if (status === 401 || status === 403) {
        try { localStorage.removeItem("besson_admin_token"); } catch {}
        nav("/admin/login");
        return;
      }
      setConnError(true);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    await api.patch(`/admin/notifications/${id}/read`);
    load();
  };

  const markAllRead = async () => {
    await api.patch(`/admin/notifications/read-all`);
    load();
  };

  const handleClick = async (n) => {
    await markRead(n.id);
    if (n.link) nav(n.link);
    setOpen(false);
  };

  return (
    <div className="relative">
      <audio ref={audioRef} src={CHIME_DATA_URI} preload="auto" />
      <button onClick={() => setOpen((o) => !o)} data-testid={variant === "default" ? "notif-bell" : `notif-bell-${variant}`} aria-label={`Notifications${unread > 0 ? ` — ${unread} non lue${unread > 1 ? 's' : ''}` : ''}`} aria-expanded={open} aria-haspopup="menu" className="relative p-2 rounded-lg hover:bg-white/10 text-white">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#06B6D4] text-[#0A1128] text-[10px] font-extrabold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {connError && unread === 0 && (
          <span title="Connexion notifications perdue — vérifiez votre session" data-testid="notif-conn-error" className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-[#0A1128] animate-pulse" />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="fixed right-3 top-16 w-[min(96vw,400px)] max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-black/10 z-[9999]" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/8 sticky top-0 bg-white">
            <span className="display font-bold text-sm text-[#0A1128]">Notifications</span>
            <div className="flex items-center gap-2">
              <button onClick={markAllRead} data-testid="notif-mark-all" className="text-xs text-[#06B6D4] hover:underline">Tout marquer lu</button>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-black/5"><X size={14} /></button>
            </div>
          </div>
          {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#64748B]">Aucune notification</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-[#FAFAFA] transition-colors flex items-start gap-3 ${n.read ? "opacity-60" : ""}`}
            >
              <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${n.read ? "bg-transparent" : "bg-[#06B6D4]"}`} />
              <div className="flex-1 min-w-0">
                <div className="display font-bold text-sm text-[#0A1128] truncate">{n.title}</div>
                {n.body && <div className="text-xs text-[#475569] mt-0.5 line-clamp-2">{n.body}</div>}
                <div className="text-[10px] text-[#94a3b8] mt-1">{timeAgo(n.created_at)}</div>
              </div>
            </button>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
